import React, { useState, useEffect, useRef } from 'react';
import { useInterviewGeneration } from '../contexts/InterviewGenerationContext';
import { useSettings } from '../contexts/SettingsContext';

/**
 * InterviewGenerationBubble — Floating pill / expandable mini panel for AI interview generation progress.
 *
 * Modeled after SyncStatusBubble. Shows at bottom-right corner on all pages while
 * interview questions are being generated in the background.
 *
 * Behavior:
 * - When generating: shows collapsed pill "Generating Questions..." (expandable to log panel)
 * - When complete: shows "Questions Ready" pill, auto-dismisses after 8s
 * - When failed: shows "Generation Failed" pill, auto-dismisses after 8s
 * - No active generation: hidden
 */
const InterviewGenerationBubble = () => {
  const { generating, logs, phase, candidateInfo } = useInterviewGeneration();
  const { settings } = useSettings();
  const secondaryColor = settings?.theme?.colors?.secondary || '#F97316';

  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const logsEndRef = useRef(null);
  const isHoveringRef = useRef(false);
  const dismissTimerRef = useRef(null);

  // Auto-scroll logs
  useEffect(() => {
    if (expanded && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [expanded, logs?.length]);

  // Auto-dismiss after complete/error — only if user is not hovering/interacting
  useEffect(() => {
    if (phase === 'complete' || phase === 'error') {
      const tryDismiss = () => {
        if (isHoveringRef.current || expanded) {
          dismissTimerRef.current = setTimeout(tryDismiss, 3000);
          return;
        }
        setDismissed(true);
      };
      dismissTimerRef.current = setTimeout(tryDismiss, 8000);
      return () => { if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current); };
    }
    setDismissed(false);
  }, [phase, expanded]);

  // Reset dismissed when new generation starts
  useEffect(() => {
    if (generating) {
      setDismissed(false);
      setExpanded(true); // auto-expand when generation starts
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    }
  }, [generating]);

  // Nothing to show
  if (!generating && phase !== 'complete' && phase !== 'error') return null;
  if (dismissed) return null;
  if (logs.length === 0) return null;

  const isComplete = phase === 'complete';
  const isError = phase === 'error';

  return (
    <div
      className="fixed bottom-6 right-6 z-50"
      style={{ maxWidth: '380px' }}
      onMouseEnter={() => { isHoveringRef.current = true; }}
      onMouseLeave={() => { isHoveringRef.current = false; }}
    >
      {/* Collapsed pill */}
      {!expanded && (
        <button
          onClick={() => setExpanded(true)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg border transition-all hover:shadow-xl ${
            isComplete ? 'bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-800'
            : isError ? 'bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-800'
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          }`}
        >
          {isComplete ? (
            <span className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </span>
          ) : isError ? (
            <span className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          ) : (
            <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: secondaryColor }}>
              <div className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          <span className={`text-sm font-medium ${
            isComplete ? 'text-green-700 dark:text-green-300'
            : isError ? 'text-red-700 dark:text-red-300'
            : 'text-gray-700 dark:text-gray-300'
          }`}>
            {isComplete ? 'Questions Ready' : isError ? 'Generation Failed' : 'Generating Questions...'}
          </span>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      )}

      {/* Expanded detail panel */}
      {expanded && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden w-[380px]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Interview Generation Progress</h4>
              {candidateInfo?.name && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                  {candidateInfo.name}{candidateInfo.jobTitle ? ` · ${candidateInfo.jobTitle}` : ''}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setExpanded(false)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {(isComplete || isError) && (
                <button onClick={() => setDismissed(true)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="px-4 pt-3 pb-1">
            {(() => {
              const totalSteps = 9;
              const completedSteps = isComplete ? totalSteps : isError ? logs.length : Math.max(0, logs.length - 1);
              const pct = Math.round((completedSteps / totalSteps) * 100);
              return (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      {isComplete ? 'Completed' : isError ? 'Failed' : `Step ${logs.length} of ${totalSteps}`}
                    </span>
                    <span className="text-xs font-semibold" style={{ color: isError ? '#EF4444' : isComplete ? '#22C55E' : secondaryColor }}>{pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 ease-out"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: isError ? '#EF4444' : isComplete ? '#22C55E' : secondaryColor,
                      }}
                    />
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Generation Log */}
          <div className="px-4 py-3 max-h-48 overflow-y-auto">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Generation Log</p>
            <div className="space-y-1.5">
              {logs.map((log, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <span className="mt-0.5 flex-shrink-0">
                    {idx === logs.length - 1 && !isComplete && !isError ? (
                      <div className="w-3 h-3 rounded-full flex items-center justify-center" style={{ backgroundColor: `${secondaryColor}20` }}>
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: secondaryColor }} />
                      </div>
                    ) : (
                      <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-400">{log}</span>
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewGenerationBubble;
